import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, Mail, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const playerRef = useRef(null);
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  // Fade out the loading overlay after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setVideoLoaded(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  // Initialize YouTube player with 0.5x speed
  useEffect(() => {
    // Load YouTube API
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    // Create player when API is ready
    window.onYouTubeIframeAPIReady = () => {
      playerRef.current = new window.YT.Player('youtube-bg', {
        videoId: 'UxqUIZ6kBTI',
        playerVars: {
          autoplay: 1,
          mute: 1,
          loop: 1,
          playlist: 'UxqUIZ6kBTI',
          controls: 0,
          showinfo: 0,
          rel: 0,
          iv_load_policy: 3,
          modestbranding: 1,
          playsinline: 1,
          disablekb: 1,
          fs: 0,
          vq: 'hd2160' // 4K
        },
        events: {
          onReady: (event) => {
            event.target.setPlaybackRate(0.5); // 0.5x speed
            event.target.playVideo();
          }
        }
      });
    };

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success("Connexion réussie !");
      navigate("/");
    } catch (error) {
      const message = error.response?.data?.detail || "Identifiants incorrects";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col bg-black">
      {/* Loading overlay - fades out after 4s */}
      <div 
        className="absolute inset-0 bg-black transition-opacity duration-1000 ease-out pointer-events-none"
        style={{ 
          zIndex: 100,
          opacity: videoLoaded ? 0 : 1
        }}
      />

      {/* Background video - YouTube Matrix animation (4K, 0.5x speed) */}
      {/* Full width, maintain ratio, crop top if taller than screen */}
      <div className="absolute inset-0 overflow-hidden" style={{ zIndex: 1 }}>
        <div 
          id="youtube-bg"
          className="absolute pointer-events-none"
          style={{ 
            width: '100vw',
            height: '56.25vw', /* 16:9 ratio = 100/16*9 */
            minHeight: '100vh',
            bottom: 0,
            left: 0
          }}
        />
      </div>
      
      {/* Black overlay 40% - more transparent to see video better */}
      <div 
        className="absolute inset-0 bg-black/40"
        style={{ zIndex: 2 }}
      />
      
      {/* Bottom gradient overlay - opaque at bottom, transparent at 30% height */}
      <div 
        className="absolute inset-x-0 bottom-0"
        style={{ 
          zIndex: 3,
          height: '30vh',
          background: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.8) 40%, rgba(0,0,0,0) 100%)'
        }}
      />

      {/* Content */}
      <div className="relative flex-1 flex flex-col items-center justify-center px-4" style={{ zIndex: 10 }}>
        {/* Login form */}
        <div className="w-full max-w-sm space-y-8 glass-card p-8 rounded-lg fade-in">
          <div className="flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_quote-genius-vert/artifacts/yaw6rlun_LOGO-Braux-300px-email.png" 
              alt="Braux Paysagistes" 
              className="h-16"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </div>

          <div className="text-center">
            <h2 className="text-3xl font-serif tracking-tight text-white">
              Calculateur de devis
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
                <Input
                  type="email"
                  placeholder="Adresse e-mail"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full h-14 pl-12 pr-4 bg-zinc-900/50 border border-zinc-800 focus:border-white focus:ring-0 rounded-sm text-white placeholder:text-zinc-500 transition-colors duration-300"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Mot de passe"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full h-14 pl-12 pr-12 bg-zinc-900/50 border border-zinc-800 focus:border-white focus:ring-0 rounded-sm text-white placeholder:text-zinc-500 transition-colors duration-300"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-white text-black hover:bg-zinc-200 rounded-sm font-medium text-base transition-all duration-300 disabled:opacity-50"
              data-testid="submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-black border-t-transparent" />
                  Chargement...
                </span>
              ) : (
                "Se connecter"
              )}
            </Button>
          </form>
        </div>
      </div>

      {/* Footer at the very bottom */}
      <div className="relative z-20 py-4 px-6">
        <div className="flex items-center justify-between text-xs text-zinc-400">
          <span>© {currentYear} Braux SA. Paysagistes depuis 200 ans.</span>
          <a 
            href="https://nexus-conseil.ch" 
            target="_blank" 
            rel="noopener noreferrer"
            className="hover:text-zinc-200 transition-colors"
          >
            made with ❤️ by nexus
          </a>
        </div>
      </div>
    </div>
  );
}
